import { Helmet } from 'react-helmet-async';
import Breadcrumbs from 'components/Breadcrumbs';
import { useLocation } from 'react-router-dom';
import { Button } from 'utils/Button';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaMapMarkerAlt,
  FaTools,
  FaHome,
  FaClock,
  FaChevronDown
} from 'react-icons/fa';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { slugify } from '../utils/cityServiceAreaData';

const banner =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597822/oak-and-stone-remodel/landing/contact-where.webp';

const ctaBg =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_1200,q_auto,f_auto/v1751597871/oak-and-stone-remodel/show/show18.webp';

const serviceAreas = {
  'Box Elder County': [
    'Tremonton',
    'Brigham City',
    'Garland',
    'Bear River City',
    'Corinne',
    'Deweyville',
    'Honeyville',
    'Elwood',
    'Fielding',
    'Bothwell',
    'Collinston',
    'Riverside',
    'Plymouth',
    'Portage',
    'Willard',
    'Mantua',
    'Perry',
    'Snowville',
    'Howell',
    'Thatcher',
    'Penrose',
    'South Willard',
    'Beaver Dam',
    'Park Valley',
    'Grouse Creek',
    'Yost',
    'Rosette',
    'Promontory',
    'Blue Creek'
  ],
  'Cache County': [
    'Logan',
    'Smithfield',
    'Hyrum',
    'Mendon',
    'Wellsville',
    'Providence',
    'Hyde Park',
    'North Logan',
    'Nibley',
    'Clarkston',
    'Newton',
    'Lewiston',
    'Millville',
    'Paradise',
    'Trenton',
    'Richmond',
    'Cache Junction',
    'Amalga',
    'Benson',
    'Cornish',
    'Cove',
    'Petersboro',
    'Avon',
    'College Ward',
    'Young Ward',
    'River Heights'
  ],
  'Weber County': [
    'Ogden',
    'Roy',
    'Plain City',
    'Farr West',
    'Harrisville',
    'North Ogden',
    'Pleasant View',
    'West Haven',
    'Hooper',
    'Marriott-Slaterville',
    'South Ogden',
    'Riverdale',
    'Uintah',
    'Washington Terrace',
    'South Weber'
  ],
  'Davis County': [
    'Layton',
    'Kaysville',
    'Syracuse',
    'Clearfield',
    'West Point',
    'Clinton',
    'Sunset',
    'Farmington',
    'Fruit Heights'
  ],
  'Rich County': ['Garden City', 'Laketown', 'Randolph', 'Woodruff']
};

const features = [
  {
    icon: FaMapMarkerAlt,
    title: '5 Counties Served',
    description: 'Weber, Davis, Box Elder, Cache, and Rich counties in Utah'
  },
  {
    icon: FaTools,
    title: 'Full Service Remodeling',
    description: 'Kitchen, bathroom, and complete home renovations'
  },
  {
    icon: FaHome,
    title: 'Custom Solutions',
    description: 'Tailored designs and premium craftsmanship for your home'
  },
  {
    icon: FaClock,
    title: '35+ Years Experience',
    description: 'Decades of trusted service in Northern Utah'
  }
];

const getCountySlug = (county) => slugify(county.replace(/ county$/i, ''));

const CountyAccordion = ({ county, cities }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={false}
      className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex justify-between items-center px-4 py-6 w-full text-left rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
        <h3 className="text-xl font-semibold text-gray-900">{county}</h3>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-gray-500">
          <FaChevronDown className="w-5 h-5" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden">
            <div className="px-4 pb-6">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                {cities.map((city) => (
                  <Link
                    key={city}
                    to={`/service-area/${getCountySlug(county)}/${slugify(
                      city
                    )}`}
                    onClick={() =>
                      console.log(
                        'Navigating to',
                        `/service-area/${getCountySlug(county)}/${slugify(
                          city
                        )}`
                      )
                    }
                    className="flex items-center p-2 rounded-lg transition-colors hover:bg-blue-50 group">
                    <FaMapMarkerAlt className="mr-2 w-4 h-4 text-blue-500 group-hover:text-blue-700" />
                    <span className="font-medium text-gray-700 group-hover:text-blue-900">
                      {city}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const ServiceArea = () => {
  const location = useLocation();

  return (
    <div className="overflow-hidden w-full">
      <Helmet>
        <title>
          Home Remodeling in Weber, Davis & Box Elder Counties | Oak & Stone
          Construction
        </title>
        <meta
          name="description"
          content="Oak & Stone Construction provides expert kitchen and bathroom remodeling in Ogden, Layton, Brigham City, Roy, North Ogden, South Ogden, Washington Terrace, Pleasant View, Riverdale, Bountiful, Kaysville, Clearfield, Farmington, Syracuse, Centerville, Tremonton, Perry, Willard, and Garland. Specializing in custom cabinets and full home renovations. Get your free consultation today!"
        />
        {/* Open Graph Meta Tags */}
        <meta
          property="og:title"
          content="Home Remodeling in Weber, Davis & Box Elder Counties | Oak & Stone Construction"
        />
        <meta
          property="og:description"
          content="Oak & Stone Construction provides expert kitchen and bathroom remodeling in Ogden, Layton, Brigham City, Roy, North Ogden, South Ogden, Washington Terrace, Pleasant View, Riverdale, Bountiful, Kaysville, Clearfield, Farmington, Syracuse, Centerville, Tremonton, Perry, Willard, and Garland. Specializing in custom cabinets and full home renovations. Get your free consultation today!"
        />
        <meta
          property="og:image"
          content="https://res.cloudinary.com/dwoubdppz/image/upload/v1751597822/oak-and-stone-remodel/landing/contact-where.webp"
        />
        <meta
          property="og:url"
          content="https://oakandstoneremodel.com/service-area"
        />
        <meta property="og:type" content="website" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Home Remodeling in Weber, Davis & Box Elder Counties | Oak & Stone Construction"
        />
        <meta
          name="twitter:description"
          content="Oak & Stone Construction provides expert kitchen and bathroom remodeling in Ogden, Layton, Brigham City, Roy, North Ogden, South Ogden, Washington Terrace, Pleasant View, Riverdale, Bountiful, Kaysville, Clearfield, Farmington, Syracuse, Centerville, Tremonton, Perry, Willard, and Garland. Specializing in custom cabinets and full home renovations. Get your free consultation today!"
        />
        <meta
          name="twitter:image"
          content="https://res.cloudinary.com/dwoubdppz/image/upload/v1751597822/oak-and-stone-remodel/landing/contact-where.webp"
        />
        <meta
          name="twitter:url"
          content="https://oakandstoneremodel.com/service-area"
        />
        <link
          rel="canonical"
          href="https://oakandstoneremodel.com/service-area"
        />
      </Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Home Remodeling in Weber, Davis & Box Elder Counties | Oak & Stone Construction',
          url: 'https://oakandstoneremodel.com/service-area',
          description:
            'Oak & Stone Construction provides expert kitchen and bathroom remodeling in Ogden, Layton, Brigham City, Roy, North Ogden, South Ogden, Washington Terrace, Pleasant View, Riverdale, Bountiful, Kaysville, Clearfield, Farmington, Syracuse, Centerville, Tremonton, Perry, Willard, and Garland. Specializing in custom cabinets and full home renovations. Get your free consultation today!'
        })}
      </script>

      {/* Hero Section */}
      <div className="relative h-[60vh] w-full">
        <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/60 via-black/40 to-black/60" />
        <img
          src={banner}
          className="object-cover absolute inset-0 w-full h-full"
          alt="A kitchen remodel built by Oak and Stone in Ogden, Utah, featuring custom cabinets and marble countertops."
        />
        <div className="flex relative z-20 flex-col justify-center items-center px-4 h-full text-white">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6 text-4xl font-bold tracking-wider text-center uppercase md:text-5xl lg:text-6xl">
            Service Area
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-6 w-32 h-1 bg-blue-500"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="max-w-3xl text-xl font-light text-center md:text-2xl">
            Oak & Stone Construction proudly serves homeowners throughout
            Northern Utah. Explore our interactive map below to see all the
            communities we serve.
          </motion.p>
        </div>
        <Breadcrumbs pathname={location.pathname} />
      </div>

      {/* Map Section */}
      <div className="px-4 py-16 bg-white">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="overflow-hidden bg-white rounded-xl shadow-lg">
            <div className="h-[60vh] md:h-auto md:aspect-video">
              <iframe
                src="https://www.google.com/maps/d/u/0/embed?mid=1C9TRHcf1DcGlPQg2P__ebINF6VP_XJk&ehbc=2E312F"
                width="100%"
                height="100%"
                title="Oak and Stone Remodel Service Area Map"
                loading="lazy"
                style={{ border: 0 }}
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full">
                Your browser does not support iframes. Please visit our Google
                Maps page directly.
              </iframe>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Service Areas Section */}
      <div className="px-4 py-16 bg-gray-50">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
              Communities We Serve
            </h2>
            <div className="mx-auto mb-6 w-24 h-1 bg-blue-500" />
            <p className="mx-auto max-w-2xl text-lg text-gray-600">
              Explore the cities and towns where we provide our expert home
              remodeling services. Click on each county to see the complete list
              of communities.
            </p>
          </motion.div>

          <div className="overflow-hidden bg-white rounded-xl shadow-sm">
            {Object.entries(serviceAreas).map(([county, cities]) => (
              <CountyAccordion key={county} county={county} cities={cities} />
            ))}
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="px-4 py-16 bg-white">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
              Our Service Details
            </h2>
            <div className="mx-auto mb-6 w-24 h-1 bg-blue-500" />
          </motion.div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-8 bg-gray-50 rounded-lg shadow-sm transition-shadow duration-300 hover:shadow-md">
                <feature.icon className="mb-4 w-10 h-10 text-blue-600" />
                <h3 className="mb-3 text-xl font-semibold text-gray-900">
                  {feature.title}
                </h3>
                <p className="leading-relaxed text-gray-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={ctaBg}
            alt="Beautiful home remodel by Oak and Stone"
            className="object-cover w-full h-full"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        {/* Content */}
        <div className="relative px-4 py-24">
          <div className="max-w-[1200px] mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mx-auto max-w-3xl">
              <h2 className="mb-8 text-3xl font-bold text-white md:text-5xl">
                Ready to Transform Your Home?
              </h2>
              <p className="mb-12 text-lg leading-relaxed text-gray-200 md:text-xl">
                Whether you're in Ogden, Layton, Brigham City, or anywhere in
                between, we're here to bring your vision to life with expert
                craftsmanship and dedication to quality.
              </p>
              <Button
                to="/contact"
                text="SCHEDULE YOUR FREE CONSULTATION"
                classname="px-8 py-4 text-lg font-semibold tracking-wide text-gray-900 bg-white hover:bg-gray-100"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};
